import type { Id } from "@convex/_generated/dataModel";
import { Info } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";

import { toConvexTableId } from "~/lib/convex-table-id";

import { Combobox } from "../../../ui/combobox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { MultiCombobox } from "../../../ui/multi-combobox";
import { Switch } from "../../../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import type {
  CategoryForMenu,
  ItemDataForForm,
  ItemFormData,
  TaxCategoryForForm,
} from "./item-form-types";

// -- Reporting Section (Sales Category + Tax Category) --

interface ReportingSectionProps {
  form: UseFormReturn<ItemFormData, unknown, ItemFormData>;
  salesCategories: Array<{ _id: string; name: string }>;
  taxCategories: TaxCategoryForForm[];
  taxCategoriesList: Array<{
    _id: Id<"taxCategories">;
    name: string;
    value: number;
  }>;
  taxCategoryDefault: {
    _id: Id<"taxCategories">;
    name: string;
    value: number;
  };
  onCreateSalesCategory: (name: string) => void;
  onCreateTaxCategory: (name: string) => void;
}

export const ReportingSection = ({
  form,
  salesCategories,
  taxCategories,
  taxCategoriesList,
  taxCategoryDefault,
  onCreateSalesCategory,
  onCreateTaxCategory,
}: ReportingSectionProps) => (
  <div className="mt-10 space-y-5">
    <div className="space-y-1">
      <h3 className="subtitle-sm text-foreground">Reporting</h3>
      <p className="body-sm text-muted-foreground">
        Choose how this item is displayed in reporting
      </p>
    </div>

    <div className="flex w-full space-x-5">
      <div className="w-full space-y-2">
        <Label className="flex items-center gap-1">
          Sales category
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="cursor-help">
                <Info className="size-4" />
                <span className="sr-only">
                  Hover for additional information
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Determines the reporting category</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <FormField
          control={form.control}
          name="salesCategoryId"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Combobox
                  className="w-full"
                  createNewOption={{
                    label: "Create new category",
                    onClick: onCreateSalesCategory,
                  }}
                  dropdownClassName="w-96"
                  items={salesCategories.map((category) => ({
                    label: category.name,
                    value: category._id,
                  }))}
                  onValueChange={field.onChange}
                  placeholder="Sales category name..."
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="w-full space-y-2">
        <Label className="flex items-center gap-1">
          Tax category
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="cursor-help">
                <Info className="size-4" />
                <span className="sr-only">
                  Hover for additional information
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Determines tax rate on this item at checkout</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <FormField
          control={form.control}
          name="taxCategoryId"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Combobox
                  className="w-full"
                  createNewOption={{
                    label: "Create new category",
                    onClick: onCreateTaxCategory,
                  }}
                  dropdownClassName="w-96"
                  items={
                    taxCategories?.length
                      ? taxCategoriesList.map((category) => ({
                          label: `${category.name} ${category.value} %`,
                          value: category._id,
                        }))
                      : [
                          {
                            label: `${taxCategoryDefault.name} ${taxCategoryDefault.value} %`,
                            value: taxCategoryDefault._id,
                          },
                        ]
                  }
                  onValueChange={field.onChange}
                  placeholder="Tax category name..."
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  </div>
);

// -- Menu Categories Section --

interface CategoriesSectionProps {
  form: UseFormReturn<ItemFormData, unknown, ItemFormData>;
  categories: CategoryForMenu[];
  itemData: ItemDataForForm;
}

export const CategoriesSection = ({
  form,
  categories,
  itemData,
}: CategoriesSectionProps) => (
  <div className="mt-10 space-y-5">
    <div className="space-y-1">
      <h3 className="subtitle-sm text-foreground">Menu Categories</h3>
      <p className="body-sm text-muted-foreground">
        Customize names, price and description
      </p>
    </div>

    <div className="space-y-2">
      <Label className="flex items-center gap-1">
        Add to menu categories
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="cursor-help">
              <Info className="size-4" />
              <span className="sr-only">Hover for additional information</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Is this item available for order</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>
      <FormField
        control={form.control}
        name="categories"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <MultiCombobox
                groups={["default"]}
                disabled={!categories?.length}
                items={{
                  default: categories
                    .filter((cat) =>
                      itemData.categoryRelations.some(
                        (c) => c.category._id === cat.id
                      )
                    )
                    .map((category) => ({
                      value: category.id,
                      label: category.name,
                      component: (
                        <div className="w-full space-y-1">
                          <div className="flex w-full items-baseline gap-1">
                            <p className="text-foreground">{category.name}</p>
                            {category.itemCount !== 0 && (
                              <p className="body-sm text-muted-foreground">
                                {category.itemCount} item
                                {category.itemCount !== 1 && "s"}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {category.menuRelations?.map((menuRelation) => (
                              <div
                                className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                                key={menuRelation.menu.id}
                              >
                                {menuRelation.menu.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      ),
                    })),
                }}
                onValueChange={(d) =>
                  field.onChange(
                    d.default.map((id) => toConvexTableId("categories", id))
                  )
                }
                values={{
                  default: field.value.map((id) => String(id)),
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  </div>
);

// -- Online Ordering & Availability Section --

interface OnlineOrderingSectionProps {
  form: UseFormReturn<ItemFormData, unknown, ItemFormData>;
  canEditStockStatus: boolean;
}

export const OnlineOrderingSection = ({
  form,
  canEditStockStatus,
}: OnlineOrderingSectionProps) => (
  <>
    <div>
      <Label className="flex items-center gap-1">
        Availability status
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="cursor-help">
              <Info className="size-4" />
              <span className="sr-only">Hover for additional information</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Indicate if this item is available to order in POS</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>
      <FormField
        control={form.control}
        name="isOutOfStock"
        render={({ field }) => (
          <FormItem className="mt-5">
            <FormControl>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={field.value}
                  disabled={!canEditStockStatus}
                  onCheckedChange={field.onChange}
                />
                <Label
                  className={canEditStockStatus ? "" : "text-muted-foreground"}
                  htmlFor="isOutOfStock"
                >
                  Mark as out of stock
                </Label>
              </div>
            </FormControl>
            {!canEditStockStatus && (
              <p className="text-muted-foreground text-xs">
                You don't have permission to change stock status
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>

    <div className="mt-8 space-y-5">
      <div className="space-y-1">
        <h3 className="subtitle-sm text-foreground">Online Ordering</h3>
        <p className="body-sm text-muted-foreground">
          Control how this item appears in your online storefront
        </p>
      </div>
      <FormField
        control={form.control}
        name="availableOnline"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor="availableOnline">
                  Available for online ordering
                </Label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {form.watch("availableOnline") && (
        <FormField
          control={form.control}
          name="onlinePrice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1">
                Online price override
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-help">
                      <Info className="size-4" />
                      <span className="sr-only">
                        Hover for additional information
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Leave empty to use the standard price</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FormLabel>
              <FormControl>
                <div className="relative w-48">
                  <span className="body-sm text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2">
                    $
                  </span>
                  <Input
                    className="body-sm px-3 py-2 pl-8"
                    inputMode="decimal"
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(/[^\d.]/g, "");
                      const parts = inputValue.split(".");
                      if (parts.length > 2) {
                        return;
                      }
                      if (parts[0] && parts[0].length > 6) {
                        return;
                      }
                      if (parts[1] && parts[1].length > 2) {
                        return;
                      }
                      field.onChange(inputValue);
                    }}
                    placeholder="Use standard price"
                    type="text"
                    value={field.value}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  </>
);
